import * as React from 'react';
import { isNil, isNumber, isString } from 'lodash';

import { Link } from 'libs/router';

import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import Tooltip from 'components/Tooltip';
import InfluencerVerificationLabel from 'components/influencer-verification-label/InfluencerVerificationLabel';

import MentionCardContextMenu from '../mention-card-context-menu/MentionCardContextMenu';

import { __t, applyTranslations } from 'utils/language';

import './mention-card-header.scss';

import type { ChannelTypes, MentionCardModalItem, MentionCardTooltipType } from 'types';

applyTranslations(require('./mention-card-header.i18n.json'));

type Props = {
  post: MentionCardModalItem,
  hideExternalLink?: boolean
};

class MentionCardHeader extends React.PureComponent<Props> {
  getUpdatedTooltipType(tooltipType: MentionCardTooltipType): string {
    const { post } = this.props;

    return isNumber(post.profileId) ? 'profile' : tooltipType;
  }

  getUrl(): string | undefined {
    const { post } = this.props;

    return isNumber(post.profileId)
      ? `/content-creator/profiles/${post.profileId}`
      : post.url;
  }

  isInternalLink(): boolean {
    const { post } = this.props;

    // isString() is used to satisfy flow
    return isNumber(post.profileId) && isString(post.profileStatus);
  }

  isPostReadyForUse(): boolean {
    const { post } = this.props;

    return !isNil(post)
      && 'profileName' in post
      && 'profileId' in post
      && 'profileStatus' in post;
  }

  isValidPost(): boolean {
    const { post } = this.props;

    return !isNil(post)
      && !isNil(post.profileName)
      && isNumber(post.profileId)
      && isString(post.profileStatus);
  }

  renderAvatar(tooltipType: MentionCardTooltipType): React.ReactNode {
    const { post } = this.props;

    if (!post || !post.profileImageUrl) {
      return null;
    }

    const url = this.getUrl();

    const entity = {
      image: {
        url: post.profileImageUrl
      }
    };

    return (
      <div className="mention-card-header__avatar">
        <Tooltip
          message={__t(`mention_card_header.${this.getUpdatedTooltipType(tooltipType)}.tooltip_goto`)}
          width="short"
        >
          <EntityAvatar
            entity={entity}
            link={url}
            customSize={{ width: 32, height: 32 }}
            openInNewTab
            bordered
          />
        </Tooltip>
      </div>
    );
  }

  renderUsername(username: string, tooltipType: MentionCardTooltipType): React.ReactNode {
    const url = this.getUrl();
    const tooltip = __t(`mention_card_header.${this.getUpdatedTooltipType(tooltipType)}.tooltip_goto`);

    return (
      <div className="mention-card-header__channel">
        <Tooltip
          message={`${tooltip}<br>${username}`}
          width="auto-compact"
        >
          <a
            href={url}
            className="mention-card-header__link text-overflow-ellipsis"
            target="_blank"
            rel="noopener noreferrer"
          >
            {username}
          </a>
        </Tooltip>
      </div>
    );
  }

  renderIconLink(channel: ChannelTypes): React.ReactNode {
    const { post, hideExternalLink } = this.props;
    const url = this.getUrl();
    const isInternalLink = this.isInternalLink();

    return (
      <div className="mention-card-header__link-container">
        {
          this.isPostReadyForUse() && isInternalLink &&
            this.renderInternalIconLink()
        }
        {
          this.isPostReadyForUse() && isNil(post.profileId) && !isInternalLink && !hideExternalLink &&
            this.renderExternalIconLink(url, channel)
        }
      </div>
    );
  }

  renderInternalIconLink(): React.ReactNode {
    const { post } = this.props;

    return (
      <Link
        to={`/content-creator/profiles/${String(post.profileId)}`}
        target="_blank"
        className="mention-card-header__link mention-card-header__link--sub"
      >
        {__t('mention_card_header.profile.label_view')}
        <div className="mention-card-header__link-icon">
          <InfluencerVerificationLabel
            withMargin
            type={post.profileStatus === 'verified' ? 'verified' : 'unverified'}
          />
        </div>
      </Link>
    );
  }

  renderExternalIconLink(url: string | undefined | null, channel: ChannelTypes): React.ReactNode {
    if (isNil(url)) {
      return null;
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mention-card-header__link mention-card-header__link--sub"
      >
        <Tooltip
          message={__t(`mention_card_header.${channel}.tooltip_goto`)}
          width="short"
        >
          {__t(`mention_card_header.${channel}.label_view`)}
        </Tooltip>
      </a>
    );
  }

  renderContextMenu(): React.ReactNode | undefined | null {
    const { post } = this.props;

    return this.isInternalLink() && (
      <div className="mention-card-header__context-menu">
        <MentionCardContextMenu
          influencerId={post.profileId}
        />
      </div>
    );
  }
}

export default MentionCardHeader;
